import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import DailyAverageProductionBaseInterface from '@app/Interface/Report/DailyAverageProductionBaseInterface';
import { Title } from '@patternfly/react-core'
import ItemLoader from '@app/Components/ItemLoader';
import moment from 'moment-timezone';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';
import TabulatorAccessors from '@app/utils/TabulatorAccessors';

interface DailyAverageProductionBaseReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    setReportTableRef: React.Dispatch<React.SetStateAction<React.MutableRefObject<null> | null>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    loading: boolean;
}

interface DailyAverageProductionBaseReportInterface {
    description: string;
    value: string;
}

const DailyAverageProductionBaseReport: React.FC<DailyAverageProductionBaseReportProps> = ({ pageId, setLastUpdatedText, setReportTableRef, setLoading, loading }) => {
    const { userState } = React.useContext(UserContext);

    // Update the parent report page's download CSV function to tap into our report's tabulator ref
    const tableRef = React.useCallback(node => {
        setReportTableRef({current: node});
    }, []);
    const [reportData, setReportData] = React.useState<DailyAverageProductionBaseReportInterface[]>([]);
    const [reportDate, setReportDate] = React.useState(moment().startOf('month'))

    const tabulatorColumns = [
        { title: 'Base Measure Description', field: 'description', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Base Measure Value', field: 'value', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
    ];

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'NDA_Current_Prod_Base')
            .then(text => { if (mounted) setLastUpdatedText(text) });

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        async function fetchReportData() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await ReportDataHelper.getDailyAverageProductionBaseData(
                    controller,
                    userState.user.currentFpuId,
                    pageId
                );
                const data: DailyAverageProductionBaseInterface = rows[0] ?? null;
                const reportRows: DailyAverageProductionBaseReportInterface[] = [];
                if (data) {
                    reportRows.push({
                        description: `Active Base`,
                        value: `${data.totalActiveBase ? data.totalActiveBase.toLocaleString('en-US') : 0} lbs`
                    });
                    reportRows.push({
                        description: `Deferred Base`,
                        value: `${data.deferredBase ? data.deferredBase.toLocaleString('en-US') : 0} lbs`
                    });
                    reportRows.push({
                        description: `Pasco Capacity Base`,
                        value: `${data.pascoCapacityBase ? data.pascoCapacityBase.toLocaleString('en-US') : 0} lbs`
                    });
                    reportRows.push({
                        description: `Total Base`,
                        value: `${data.totalBase ? data.totalBase.toLocaleString('en-US') : 0} lbs`
                    });
                }
                if (mounted) {
                    setReportData(reportRows);
                    setLoading(false);
                }
            } else {
                if (mounted) {
                    setReportData([]);
                    setLoading(false);
                }
            }
        }

        setLoading(true);
        fetchReportData();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id, userState.user.currentFpuId]);

    if (loading) {
        return <ItemLoader />;
    }

    if (reportData.length === 0) {
        return <p><em>No report data to show for the selected producer</em></p>;
    }

    return (
        <>
            <Title headingLevel="h4" className="pf-u-mb-lg">Daily Average Production Base as of {reportDate.format('MMMM D, YYYY')}</Title>
            <SCTabulator
                tableRef={tableRef}
                data={reportData}
                columns={tabulatorColumns}
                layout="fitColumns"
                options={{
                    ...options('nda-report-daily-average-production-base-sc', true, false),
                    headerVisible: false,
                    downloadConfig: {columnHeaders: false},
                }}
            />
        </>
    );
};

export default DailyAverageProductionBaseReport;